import { LeafletContextInterface, createElementObject } from '@react-leaflet/core';
import L from 'leaflet';
import { translateLatLngBounds } from '../Text/text-annotation-util';
import { createRectangleResizeElement } from './reactangle-annotation-resize-element';

/**
 * The rectangle annotation edit element is a composite object. It is a layer that comprises of:
 *   - Markers on each corner used to resize the image,
 *   - A drag rectangle that fills the middle used to translate the image an
 *   - An additional focus outline element to communicate the fact we are in this feature's edit state
 */
interface EditRectangleElementProps {
    bounds: L.LatLngBounds;
    rectangleElement: Readonly<{ instance: L.Rectangle; context: Readonly<{ map: L.Map }> }>;
    context: LeafletContextInterface;
}

const focusOptions: L.PolylineOptions = {
    color: 'transparent',
    weight: 3,
    interactive: false,
    fill: false,
};

const dragOptions: L.PolylineOptions = {
    color: 'transparent',
    interactive: true,
    fill: true,
    fillColor: 'transparent',
    bubblingMouseEvents: false,
};

export const createRectangleEditElement = (props: EditRectangleElementProps, context: LeafletContextInterface) => {
    const rectangleEditLayer = new L.LayerGroup();
    const rectangleEditLayerElement = createElementObject<L.LayerGroup>(rectangleEditLayer, context);

    const focusOutline = new L.Rectangle(props.rectangleElement.instance.getBounds(), focusOptions);
    const focusOutlineElement = createElementObject<L.Rectangle>(focusOutline, context);

    const dragRectangle = new L.Rectangle(props.rectangleElement.instance.getBounds(), dragOptions);
    const dragRectangleElement = createElementObject<L.Rectangle>(dragRectangle, context);

    const resizeElement = createRectangleResizeElement({ rectangleElement: props.rectangleElement, context }, context);

    props.rectangleElement.instance.on('update', () => {
        dragRectangleElement.instance.setBounds(props.rectangleElement.instance.getBounds());
        focusOutlineElement.instance.setBounds(props.rectangleElement.instance.getBounds());
    });

    props.rectangleElement.instance.on('remove', () => {
        rectangleEditLayerElement.instance.fireEvent('remove');
    });

    rectangleEditLayerElement.instance.on('remove', () => {
        dragRectangleElement.instance.clearAllEventListeners();
        rectangleEditLayerElement.instance.clearLayers();
        L.DomUtil.removeClass(context.map.getContainer(), 'leaflet-move');
    });

    rectangleEditLayerElement.instance.on('add', () => {
        rectangleEditLayerElement.instance.addLayer(focusOutlineElement.instance);
        rectangleEditLayerElement.instance.addLayer(dragRectangleElement.instance);
        rectangleEditLayerElement.instance.addLayer(resizeElement.instance);

        dragRectangleElement.instance.on('mouseover', () => {
            L.DomUtil.addClass(context.map.getContainer(), 'leaflet-move');
        });

        dragRectangleElement.instance.on('mouseout', () => {
            L.DomUtil.removeClass(context.map.getContainer(), 'leaflet-move');
        });

        let lastLatLng: L.LatLng | undefined;
        const onDragStart = (e: L.LeafletMouseEvent) => {
            lastLatLng = e.latlng;
            dragRectangleElement.instance.on('mousemove', onDrag);
            dragRectangleElement.instance.on('mouseup', onDragEnd);
        };

        const onDrag = (e: L.LeafletMouseEvent) => {
            const imageOverlayBounds = props.rectangleElement.instance.getBounds();
            if (lastLatLng) {
                const newBounds = translateLatLngBounds(imageOverlayBounds, lastLatLng, e.latlng);
                lastLatLng = e.latlng;
                props.rectangleElement.instance.setBounds(newBounds);
                dragRectangleElement.instance.setBounds(newBounds);
                focusOutlineElement.instance.setBounds(newBounds);
                props.rectangleElement.instance.fire('update', e);
            }
        };

        const onDragEnd = () => {
            lastLatLng = undefined;
            dragRectangleElement.instance.off('mousemove', onDrag);
            dragRectangleElement.instance.off('mouseup', onDragEnd);
        };

        dragRectangleElement.instance.on('mousedown', onDragStart);
    });

    return rectangleEditLayerElement;
};
